<template>
  <div class="scientist-term-result-wrapper">
    <!-- ═══════════════════════════════════════════════════════════════
         CONTROL BAR (NO-PRINT)
         ═══════════════════════════════════════════════════════════════ -->
    <div class="no-print control-bar">
      <div class="d-flex align-items-center">
        <h4 class="mb-0 text-white font-weight-bolder mr-2">
          Scientist Term Result Report
        </h4>
        <b-badge variant="light-info" class="font-weight-bold px-2 py-1">
          Total: {{ students.length }} Student(s)
        </b-badge>
      </div>

      <div class="action-buttons">
        <button class="btn-modern btn-print" @click="printReport">
          <feather-icon icon="PrinterIcon" size="18" class="mr-50" />
          <span>Print Report</span>
        </button>
        <button class="btn-modern btn-close-btn" @click="closeReport">
          <feather-icon icon="XIcon" size="18" class="mr-50" />
          <span>Close</span>
        </button>
      </div>
    </div>

    <!-- ═══════════════════════════════════════════════════════════════
         PAGES STACK (EACH STUDENT HAS PAGE 1 AND PAGE 2)
         ═══════════════════════════════════════════════════════════════ -->
    <div class="print-container">
      <div
        v-for="(student, sIndex) in students"
        :key="student.id || sIndex"
        class="student-report-set"
      >
        <!-- ═════════════════════════════════════════════════════════════
             PAGE 1: PROGRESS DETAILS (FRONT SIDE)
             ═════════════════════════════════════════════════════════════ -->
        <div class="a4-page page-front">
          <div class="page-outer-frame">
            
            <!-- ── Top Letterhead: Logo & School Name ── -->
            <div class="report-header">
              <!-- School Logo Space (kept empty for user to update later) -->
              <div class="logo-container">
                <img
                  v-if="student.schoolLogo"
                  :src="student.schoolLogo"
                  alt="School Logo"
                  class="school-logo-img"
                />
                <div v-else class="logo-placeholder"></div>
              </div>

              <!-- Center Title & Subtitle -->
              <div class="school-title-container">
                <h1 class="school-title">The School of Scientist</h1>
                <div class="school-tagline">REGISTERED &amp; RECOGNIZED</div>
              </div>

              <!-- Top right balance spacer -->
              <div class="header-right-spacer"></div>
            </div>

            <!-- ── Student Info Section (Grey Border Box) ── -->
            <div class="student-info-section">
              <!-- Info Table (3 Columns x 3 Rows) -->
              <table class="student-info-table">
                <tbody>
                  <tr>
                    <td class="col-1">
                      <div class="field-wrap">
                        <span class="info-label">Student ID:</span>
                        <span class="info-value">{{ student.studentId }}</span>
                      </div>
                    </td>
                    <td class="col-2">
                      <div class="field-wrap">
                        <span class="info-label">Standard:</span>
                        <span class="info-value">{{ student.standard }}</span>
                      </div>
                    </td>
                    <td class="col-3">
                      <div class="field-wrap">
                        <span class="info-label">Section:</span>
                        <span class="info-value">{{ student.section }}</span>
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td class="col-1">
                      <div class="field-wrap">
                        <span class="info-label">Student's Name:</span>
                        <span class="info-value">{{ student.name }}</span>
                      </div>
                    </td>
                    <td class="col-2">
                      <div class="field-wrap">
                        <span class="info-label">Student's Age:</span>
                        <span class="info-value">{{ student.studentAge }}</span>
                      </div>
                    </td>
                    <td class="col-3">
                      <div class="field-wrap">
                        <span class="info-label">Class Avg Age:</span>
                        <span class="info-value">{{ student.classAvgAge }}</span>
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td class="col-1">
                      <div class="field-wrap">
                        <span class="info-label">Father's Name:</span>
                        <span class="info-value">{{ student.fatherName }}</span>
                      </div>
                    </td>
                    <td class="col-2">
                      <div class="field-wrap">
                        <span class="info-label">Attendance:</span>
                        <span class="info-value">{{ student.attendance }}</span>
                      </div>
                    </td>
                    <td class="col-3">
                      <div class="field-wrap">
                        <span class="info-label">Days out of:</span>
                        <span class="info-value">{{ student.daysOutOf }}</span>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>

              <!-- Student Photo on Far Right (Inside Grey Box) -->
              <div class="photo-col">
                <div class="photo-box">
                  <img
                    v-if="student.photo"
                    :src="student.photo"
                    alt="Student Photo"
                    class="student-photo-img"
                  />
                  <div v-else class="photo-placeholder">
                    <feather-icon icon="UserIcon" size="26" class="photo-icon" />
                  </div>
                </div>
              </div>
            </div>

            <!-- ── Progress Details Banner (Grey Border Box) ── -->
            <div class="progress-title-banner-box">
              <span class="banner-text">
                PROGRESS DETAILS OF {{ student.term || 'FIRST TERM 2026-2027' }}
              </span>
            </div>

            <!-- ── Parent-Teacher Meeting Box & Marks Key Table ── -->
            <div class="ptm-markskey-section">
              <!-- Left: PTM Box -->
              <div class="ptm-left-box">
                <div class="ptm-title-line">Parent-Teacher</div>
                <div class="ptm-title-line">Meeting attended:</div>
                <div class="ptm-action-row">
                  <span class="ptm-term-label">First</span>
                  <div class="ptm-dashed-box">
                    <span class="ptm-opt" :class="{ 'is-active': student.ptmAttended === 'Yes' }">Yes</span>
                    <span class="ptm-divider"></span>
                    <span class="ptm-opt" :class="{ 'is-active': student.ptmAttended === 'No' }">No</span>
                  </div>
                </div>
              </div>

              <!-- Right: Marks Key 3-Tier Header Table -->
              <div class="marks-key-right-box">
                <table class="marks-key-table">
                  <thead>
                    <tr class="mk-tier1-row">
                      <th colspan="5" class="mk-tier1-th">Marks Key</th>
                    </tr>
                    <tr class="mk-tier2-row">
                      <th colspan="3" class="mk-tier2-th">First Term</th>
                      <th colspan="2" class="mk-tier2-th">In This Section</th>
                    </tr>
                    <tr class="mk-tier3-row">
                      <th class="mk-col-cell col-w-assess">Assessment</th>
                      <th class="mk-col-cell col-w-exam">Exam</th>
                      <th class="mk-col-cell col-w-con">Consolidated Total</th>
                      <th class="mk-col-cell col-w-high">Highest Total</th>
                      <th class="mk-col-cell col-w-avg">Average Total</th>
                    </tr>
                  </thead>
                </table>
              </div>
            </div>

            <!-- ── Subjects List with Header Bars (#DBEEF4) & Remarks ── -->
            <div class="subjects-container">
              <div
                v-for="(sub, subIdx) in student.subjects"
                :key="subIdx"
                class="subject-item"
              >
                <!-- Subject Header Row (#DBEEF4 background with white marks cells) -->
                <div class="subject-bar-row">
                  <div class="subject-name-area">
                    {{ sub.name }}
                  </div>
                  <div class="subject-marks-area">
                    <div class="mark-box col-w-assess">{{ sub.assessment }}</div>
                    <div class="mark-box col-w-exam">{{ sub.exam }}</div>
                    <div class="mark-box col-w-con">{{ sub.consolidatedTotal }}</div>
                    <div class="mark-box col-w-high">{{ sub.highestTotal }}</div>
                    <div class="mark-box col-w-avg">{{ sub.averageTotal }}</div>
                  </div>
                </div>

                <!-- Subject Remark Line -->
                <div
                  class="subject-remark-line"
                  :class="{ 'urdu-remark': sub.isRtl }"
                >
                  {{ sub.remarks }}
                </div>
              </div>
            </div>

            <!-- ── General Performance Section ── -->
            <div class="performance-panel general-perf-panel">
              <div class="panel-heading">General Performance</div>
              <table class="panel-table">
                <thead>
                  <tr>
                    <th
                      v-for="(item, gpIdx) in student.generalPerformance"
                      :key="gpIdx"
                      class="panel-th"
                    >
                      {{ item.label }}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td
                      v-for="(item, gpIdx) in student.generalPerformance"
                      :key="gpIdx"
                      class="panel-td"
                    >
                      {{ item.value }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- ── Co-Curriculum Activities Section ── -->
            <div class="performance-panel cocurriculum-panel">
              <div class="panel-heading">Co-Curriculum Activities</div>
              <table class="panel-table cocurriculum-table">
                <thead>
                  <tr>
                    <th class="cc-th-label"></th>
                    <th
                      v-for="(item, ccIdx) in student.coCurriculum"
                      :key="ccIdx"
                      class="panel-th"
                    >
                      {{ item.label }}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td class="cc-td-label">Performance</td>
                    <td
                      v-for="(item, ccIdx) in student.coCurriculum"
                      :key="ccIdx"
                      class="panel-td"
                    >
                      {{ item.value }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- ── Bottom Result Summary Bar ── -->
            <div class="result-summary-bar">
              <table class="summary-table">
                <thead>
                  <tr class="summary-head-row">
                    <th>Total Marks</th>
                    <th>Obtained Marks</th>
                    <th>Percentage</th>
                    <th>Grade</th>
                    <th>Rank</th>
                    <th>Passed / Failed</th>
                  </tr>
                </thead>
                <tbody>
                  <tr class="summary-data-row">
                    <td>{{ student.summary.totalMarks }}</td>
                    <td>{{ student.summary.obtainedMarks }}</td>
                    <td>{{ student.summary.percentage }}</td>
                    <td>{{ student.summary.grade }}</td>
                    <td>{{ student.summary.rank }}</td>
                    <td class="status-badge">{{ student.summary.status }}</td>
                  </tr>
                </tbody>
              </table>
            </div>

          </div>
        </div>

        <!-- ═════════════════════════════════════════════════════════════
             PAGE 2: SELF ASSESSMENT, REMARKS, RULES (BACK SIDE)
             ═════════════════════════════════════════════════════════════ -->
        <div class="a4-page page-back">
          <div class="page-outer-frame page-back-inner">

            <!-- ── Self Assessment of Pupil ── -->
            <div class="self-assessment-wrapper">
              <div class="self-assessment-frame">
                <div class="self-assessment-title">SELF ASSESSMENT OF PUPIL</div>
                <div class="self-assessment-body">
                  {{ (student.page2 && student.page2.selfAssessmentText) || '' }}
                </div>
              </div>
            </div>

            <!-- ── Grading Key Table ── -->
            <div class="grading-key-wrapper">
              <div class="grading-key-title-bar">GRADING KEY</div>
              <table class="grading-key-table">
                <thead>
                  <tr>
                    <th class="gk-th-grade">Grade</th>
                    <th class="gk-th-pct">Percentage (%)</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(gk, gkIdx) in gradingKeyList" :key="gkIdx">
                    <td class="gk-td-grade">{{ gk.grade }}</td>
                    <td class="gk-td-pct">{{ gk.percentage }}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- ── Class Teacher's Remarks ── -->
            <div class="remarks-section class-teacher-remarks">
              <div class="remarks-title">CLASS TEACHER'S REMARKS</div>
              <div class="remarks-content">
                {{ (student.page2 && student.page2.classTeacherRemarks) || '' }}
              </div>
              <div class="remarks-sig-row">
                <div class="sig-item">
                  <span class="sig-label">Signature:</span>
                  <span class="sig-underline"></span>
                </div>
                <div class="sig-item sig-date-item">
                  <span class="sig-label">Date:</span>
                  <span class="sig-underline"></span>
                </div>
              </div>
            </div>

            <!-- ── Principal Remarks Section (#BACCE4 Background) ── -->
            <div class="remarks-section principal-remarks-box">
              <div class="remarks-title">PRINCIPAL / HEAD TEACHER'S REMARKS</div>
              <div class="remarks-content">
                {{ (student.page2 && student.page2.principalRemarks) || '' }}
              </div>
              <div class="remarks-sig-row">
                <div class="sig-item">
                  <span class="sig-label">Signature:</span>
                  <span class="sig-underline"></span>
                </div>
                <div class="sig-item sig-date-item">
                  <span class="sig-label">Date:</span>
                  <span class="sig-underline"></span>
                </div>
              </div>
            </div>

            <!-- ── Next Term Commencement Line ── -->
            <div class="next-term-section">
              <span class="next-term-txt">Next Term will Begin on:</span>
              <span class="next-term-underline">
                {{ (student.page2 && student.page2.nextTermDate) || '' }}
              </span>
            </div>

            <!-- ── Rules Section ── -->
            <div class="rules-panel">
              <div class="rules-title">RULES</div>
              <ul class="rules-ul">
                <li
                  v-for="(rule, rIdx) in rulesList"
                  :key="rIdx"
                  class="rules-li"
                >
                  <template v-if="typeof rule === 'string'">
                    {{ rule }}
                  </template>
                  <template v-else>
                    {{ rule.title }}
                    <ul class="sub-rules-ul">
                      <li
                        v-for="(subR, sRIdx) in rule.subItems"
                        :key="sRIdx"
                        class="sub-rules-li"
                      >
                        {{ subR }}
                      </li>
                    </ul>
                  </template>
                </li>
              </ul>
            </div>

            <!-- ── Parent Signature ── -->
            <div class="parent-sig-section">
              <div class="sig-item">
                <span class="sig-label">Parent's Signature:</span>
                <span class="sig-underline"></span>
              </div>
              <div class="sig-item sig-date-item">
                <span class="sig-label">Date:</span>
                <span class="sig-underline"></span>
              </div>
            </div>

          </div>
        </div>

      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "ScientistTermResult",
  data() {
    return {
      // Reference Grading Key
      gradingKeyList: [
        { grade: "A+", percentage: "90% and above" },
        { grade: "A", percentage: "80% to 89%" },
        { grade: "B", percentage: "70% to 79%" },
        { grade: "C", percentage: "60% to 69%" },
        { grade: "D", percentage: "50% to 59%" },
        { grade: "E", percentage: "33% to 49%" },
        { grade: "U", percentage: "Below 33% Fail / Ungraded" },
      ],
      // Reference Rules
      rulesList: [
        "The Progress Report must be returned within one week, signed by the parent or guardian.",
        "A fine will be charged for issuing a duplicate report in case of loss or damage.",
        "The school holds the final authority to expel any student who fails to adjust academically or otherwise.",
        "The Progress Report may be withheld if a student is caught cheating during any assessment or examination.",
        "This report remains school property; any unauthorized tampering with grades or remarks will lead to disciplinary action.",
        "For result discussions, parents must schedule a formal office appointment rather than visiting unannounced.",
        "Teacher recommendations and remedial suggestions given in this result must be actively practiced at home.",
        "Subsequent term materials or co-curricular participation may be put on hold until the signed report is safely returned.",
        {
          title: 'Complete the digital "Self Assessment" online via the school platform, keeping these core principles in mind:',
          subItems: [
            "Constructive Reflection: Review exam performance together to honestly evaluate strengths and preparation.",
            "Goal Setting: Establish practical academic and behavioral targets to build accountability and personal growth.",
          ],
        },
      ],
      // Multi-Student Dummy Data (Extensible / Replaceable via API)
      students: [
        {
          id: "1438",
          studentId: "1438",
          name: "MUHAMMAD HARAM",
          fatherName: "FAISAL",
          standard: "FIFTH",
          section: 'MARIGOLD "C"',
          studentAge: "12 YEARS",
          classAvgAge: "11 YEARS",
          attendance: "44",
          daysOutOf: "57",
          term: "FIRST TERM 2026-2027",
          schoolLogo: "", // Keep empty for user to update later
          photo: "", // Student photo URL or placeholder
          ptmAttended: "Yes",
          subjects: [
            {
              name: "Mathematics",
              assessment: "5.50/10",
              exam: "24.50/40",
              consolidatedTotal: "30.00/50",
              highestTotal: "45.75/50",
              averageTotal: "31.88/50",
              remarks:
                "He understands concepts taught, but often makes careless mistakes. It would be beneficial for him to slow down and check his work carefully.",
              isRtl: false,
            },
            {
              name: "Science",
              assessment: "7.25/10",
              exam: "24.00/40",
              consolidatedTotal: "31.25/50",
              highestTotal: "39.25/50",
              averageTotal: "32.09/50",
              remarks:
                "Performs well. He is inquisitive and highly interested in science topics. Always seeks additional information.",
              isRtl: false,
            },
            {
              name: "Computer",
              assessment: "5.25/10",
              exam: "26.75/40",
              consolidatedTotal: "32.00/50",
              highestTotal: "41.25/50",
              averageTotal: "30.11/50",
              remarks:
                "He clearly enjoys using the computing facilities, but is not yet working to his full potential.",
              isRtl: false,
            },
            {
              name: "English",
              assessment: "5.75/10",
              exam: "21.75/40",
              consolidatedTotal: "27.50/50",
              highestTotal: "38.50/50",
              averageTotal: "30.37/50",
              remarks:
                "Shows interest in all class work. Understands grammar but needs regular practice. Reading and handwriting needs improvement.",
              isRtl: false,
            },
            {
              name: "Islamiat",
              assessment: "7.00/10",
              exam: "29.75/40",
              consolidatedTotal: "36.75/50",
              highestTotal: "45.00/50",
              averageTotal: "38.86/50",
              remarks:
                "آپ کا مطالعہ کمزور ہے جبکہ ناظرہ اور خوشخطی میں کارکردگی شاندار ہے۔",
              isRtl: true,
            },
            {
              name: "Urdu",
              assessment: "7.50/10",
              exam: "26.25/40",
              consolidatedTotal: "33.75/50",
              highestTotal: "47.75/50",
              averageTotal: "39.71/50",
              remarks:
                "آپ کی اب تک کی کارکردگی بہتر ہے مزید محنت سے بہترین ہو سکتی ہے۔",
              isRtl: true,
            },
          ],
          generalPerformance: [
            { label: "Attendance", value: "5/10" },
            { label: "Participation", value: "5/5" },
            { label: "Behavior", value: "4/5" },
            { label: "Classwork", value: "4/5" },
            { label: "Homework", value: "4/5" },
            { label: "Personal Hygiene", value: "4/5" },
            { label: "M.M", value: "26/35" },
          ],
          coCurriculum: [
            { label: "Calligraphy", value: "Satisfactory" },
            { label: "IKLC", value: "-" },
            { label: "Creative Writing", value: "-" },
            { label: "IKMC", value: "-" },
            { label: "Spellathon", value: "-" },
            { label: "Project Exhibition", value: "-" },
          ],
          summary: {
            totalMarks: 335,
            obtainedMarks: "217.25",
            percentage: "64.85%",
            grade: "C",
            rank: "13th",
            status: "PASSED",
          },
          page2: {
            selfAssessmentText: "",
            classTeacherRemarks:
              "He has ability and can do much better. I would like to see him start to become a more responsible and more active participant in his own learning.",
            classTeacherSignatureDate: "",
            principalRemarks:
              "You have tried and that is the most important step towards success. Keep it up!",
            principalSignatureDate: "",
            nextTermDate: "",
            parentSignatureDate: "",
          },
        },
        {
          id: "1439",
          studentId: "1439",
          name: "FATIMA ZAHRA",
          fatherName: "MUHAMMAD USMAN",
          standard: "FIFTH",
          section: 'MARIGOLD "C"',
          studentAge: "11 YEARS",
          classAvgAge: "11 YEARS",
          attendance: "54",
          daysOutOf: "57",
          term: "FIRST TERM 2026-2027",
          schoolLogo: "",
          photo: "",
          ptmAttended: "Yes",
          subjects: [
            {
              name: "Mathematics",
              assessment: "8.50/10",
              exam: "36.00/40",
              consolidatedTotal: "44.50/50",
              highestTotal: "45.75/50",
              averageTotal: "31.88/50",
              remarks:
                "Consistently demonstrates excellent problem-solving ability and mathematical understanding.",
              isRtl: false,
            },
            {
              name: "Science",
              assessment: "9.00/10",
              exam: "37.50/40",
              consolidatedTotal: "46.50/50",
              highestTotal: "39.25/50",
              averageTotal: "32.09/50",
              remarks:
                "Extremely curious and attentive. Excels in understanding scientific principles and practicals.",
              isRtl: false,
            },
            {
              name: "Computer",
              assessment: "8.50/10",
              exam: "35.00/40",
              consolidatedTotal: "43.50/50",
              highestTotal: "41.25/50",
              averageTotal: "30.11/50",
              remarks:
                "Demonstrates great proficiency and interest in computer skills and theory.",
              isRtl: false,
            },
            {
              name: "English",
              assessment: "8.75/10",
              exam: "34.50/40",
              consolidatedTotal: "43.25/50",
              highestTotal: "38.50/50",
              averageTotal: "30.37/50",
              remarks:
                "Exceptional language expression, strong vocabulary, and neat handwriting.",
              isRtl: false,
            },
            {
              name: "Islamiat",
              assessment: "9.50/10",
              exam: "38.00/40",
              consolidatedTotal: "47.50/50",
              highestTotal: "45.00/50",
              averageTotal: "38.86/50",
              remarks:
                "ناظرہ، حفظ اور مطالعہ میں شاندار کارکردگی۔ اخلاق اور طرز عمل قابل تعریف ہے۔",
              isRtl: true,
            },
            {
              name: "Urdu",
              assessment: "9.00/10",
              exam: "37.50/40",
              consolidatedTotal: "46.50/50",
              highestTotal: "47.75/50",
              averageTotal: "39.71/50",
              remarks:
                "خوشخطی، فصاحت اور تحریری صلاحیتیں بہت شاندار ہیں۔ شاباش!",
              isRtl: true,
            },
          ],
          generalPerformance: [
            { label: "Attendance", value: "9/10" },
            { label: "Participation", value: "5/5" },
            { label: "Behavior", value: "5/5" },
            { label: "Classwork", value: "5/5" },
            { label: "Homework", value: "5/5" },
            { label: "Personal Hygiene", value: "5/5" },
            { label: "M.M", value: "34/35" },
          ],
          coCurriculum: [
            { label: "Calligraphy", value: "Excellent" },
            { label: "IKLC", value: "Winner" },
            { label: "Creative Writing", value: "1st Position" },
            { label: "IKMC", value: "High Distinction" },
            { label: "Spellathon", value: "1st Position" },
            { label: "Project Exhibition", value: "1st Position" },
          ],
          summary: {
            totalMarks: 335,
            obtainedMarks: "271.75",
            percentage: "81.12%",
            grade: "A",
            rank: "2nd",
            status: "PASSED",
          },
          page2: {
            selfAssessmentText: "",
            classTeacherRemarks:
              "An exemplary and exceptionally well-behaved pupil with an outstanding dedication to learning.",
            classTeacherSignatureDate: "",
            principalRemarks:
              "Brilliant academic accomplishment! Keep shining and inspiring others.",
            principalSignatureDate: "",
            nextTermDate: "",
            parentSignatureDate: "",
          },
        },
      ],
    };
  },
  methods: {
    printReport() {
      window.print();
    },
    closeReport() {
      if (window.history.length > 1) {
        this.$router.go(-1);
      } else {
        window.close();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
/* ═══════════════════════════════════════════════════════════════
   SCIENTIST TERM RESULT STYLES
   Exact Color Codes:
   - Light Blue: #7FD2F0
   - Border Navy: #000080
   - Subjects Row Background: #DBEEF4
   - Heading (General Performance & Co-Curriculum): #758FBE
   - Subheading (General Performance & Co-Curriculum): #000066
   - Principal Remarks Background: #BACCE4
   ═══════════════════════════════════════════════════════════════ */

$light-blue: #7FD2F0;
$border-navy: #000080;
$subject-row-bg: #DBEEF4;
$heading-gp-cc: #758FBE;
$subheading-gp-cc: #000066;
$principal-bg: #BACCE4;

.scientist-term-result-wrapper {
  background-color: #ffffff; /* White background as requested */
  min-height: 100vh;
  padding: 0 0 30px 0;
  font-family: 'Calibri', 'Segoe UI', Arial, sans-serif;
  color: #000000;
  -webkit-font-smoothing: antialiased;
}

/* ── Control Bar (Screen Only) ── */
.control-bar {
  position: sticky;
  top: 0;
  z-index: 1000;
  background-color: #1e293b;
  color: #ffffff;
  padding: 10px 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.btn-modern {
  display: inline-flex;
  align-items: center;
  border: none;
  border-radius: 4px;
  padding: 7px 15px;
  font-weight: 600;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-left: 8px;

  &.btn-print {
    background-color: #2563eb;
    color: #ffffff;
    &:hover {
      background-color: #1d4ed8;
    }
  }

  &.btn-close-btn {
    background-color: #475569;
    color: #ffffff;
    &:hover {
      background-color: #334155;
    }
  }
}

/* ── Print Container & A4 Dimensions ── */
.print-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px 0;
  gap: 30px;
  background-color: #ffffff;
}

.student-report-set {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 30px;
}

.a4-page {
  width: 210mm;
  height: 297mm;
  min-height: 297mm;
  max-height: 297mm;
  background-color: #ffffff;
  box-sizing: border-box;
  padding: 6mm 7mm;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.12);
  position: relative;
  page-break-after: always;
  break-after: page;
  overflow: hidden;
}

.page-outer-frame {
  width: 100%;
  height: 100%;
  border: 4px solid $border-navy; /* Thick navy border as per reference */
  box-sizing: border-box;
  padding: 5mm 6mm;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

/* ═══════════════════════════════════════════════════════════════
   PAGE 1 (FRONT SIDE) EXACT STYLES
   ═══════════════════════════════════════════════════════════════ */

/* ── Header: Logo, Title, Tagline ── */
.report-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 3px;
}

.logo-container {
  width: 90px;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: flex-start;
}

.school-logo-img {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

.logo-placeholder {
  width: 80px;
  height: 55px;
}

.school-title-container {
  flex: 1;
  text-align: center;
}

.school-title {
  font-family: 'Old English Text MT', 'Cinzel', 'Georgia', 'Times New Roman', serif;
  font-size: 29pt;
  font-weight: 700;
  color: $border-navy;
  margin: 0;
  line-height: 1.1;
  letter-spacing: 0.5px;
}

.school-tagline {
  font-family: 'Arial', sans-serif;
  font-size: 10pt;
  font-weight: 800;
  color: #00a2e8; /* Vivid Cyan / Light Blue */
  letter-spacing: 2px;
  margin-top: 2px;
  text-transform: uppercase;
}

.header-right-spacer {
  width: 90px; /* Balances the logo on left */
}

/* ── Student Info Section (Grey Border Box with 3 Columns) ── */
.student-info-section {
  border: 1.5px solid #8e949a; /* Distinct grey border box */
  padding: 6px 8px 6px 10px;
  margin-top: 3px;
  margin-bottom: 6px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-sizing: border-box;
}

.student-info-table {
  width: calc(100% - 85px);
  border-collapse: collapse;
  table-layout: fixed;

  td {
    padding: 3.5px 8px 3.5px 0;
    vertical-align: middle;

    &.col-1 {
      width: 38%;
      padding-right: 12px;
    }

    &.col-2 {
      width: 31%;
      padding-right: 12px;
    }

    &.col-3 {
      width: 31%;
      padding-right: 4px;
    }
  }
}

.field-wrap {
  display: flex;
  align-items: flex-end;
  width: 100%;
}

.info-label {
  font-size: 8.5pt;
  font-weight: 700;
  color: #000000;
  white-space: nowrap !important;
  margin-right: 5px;
  flex-shrink: 0;
  line-height: 1.1;
}

.info-value {
  font-size: 8.5pt;
  font-weight: 700;
  color: #000000;
  text-transform: uppercase;
  flex: 1;
  border-bottom: 1.5px solid #000000;
  text-align: center;
  white-space: nowrap !important;
  overflow: visible;
  padding-bottom: 1px;
  line-height: 1.2;
}

.photo-col {
  width: 80px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-shrink: 0;
}

.photo-box {
  width: 76px;
  height: 82px;
  border: 1px solid #758FBE;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #f1f5f9;
  overflow: hidden;
}

.student-photo-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.photo-placeholder {
  color: #94a3b8;
}

/* ── Progress Details Banner Box (Grey Border Box) ── */
.progress-title-banner-box {
  border: 2px solid #8e949a; /* Matching grey border box */
  padding: 3.5px 0;
  text-align: center;
  margin: 3px 0 6px 0;
}

.banner-text {
  font-size: 11.5pt;
  font-weight: 800;
  color: #00a2e8; /* Vivid Cyan */
  letter-spacing: 1.5px;
  text-transform: uppercase;
}

/* ── PTM Box & Marks Key 3-Tier Header ── */
.ptm-markskey-section {
  display: flex;
  align-items: stretch;
  border-top: 1.5px solid $border-navy;
  border-bottom: 1.5px solid $border-navy;
  margin-bottom: 5px;
}

.ptm-left-box {
  width: 25%;
  border-right: 1.5px solid $border-navy;
  padding: 3px 6px;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.ptm-title-line {
  font-size: 8pt;
  font-weight: 700;
  color: #000000;
  line-height: 1.15;
}

.ptm-action-row {
  display: flex;
  align-items: center;
  margin-top: 4px;
}

.ptm-term-label {
  font-size: 8pt;
  font-weight: 700;
  margin-right: 8px;
}

.ptm-dashed-box {
  border: 1px dashed $border-navy;
  display: inline-flex;
  align-items: center;
  padding: 1px 4px;
}

.ptm-opt {
  font-size: 7.5pt;
  font-weight: 700;
  padding: 0 4px;

  &.is-active {
    font-weight: 800;
  }
}

.ptm-divider {
  width: 1px;
  height: 12px;
  border-right: 1px dashed $border-navy;
  margin: 0 2px;
}

.marks-key-right-box {
  width: 75%;
}

.marks-key-table {
  width: 100%;
  border-collapse: collapse;

  th {
    text-align: center;
    font-weight: 700;
    color: #000000;
    border-left: 1px solid $border-navy;
    padding: 1px 2px;

    &:first-child {
      border-left: none;
    }
  }

  .mk-tier1-th {
    font-size: 8.5pt;
    font-weight: 800;
    border-bottom: 1px solid $border-navy;
    background-color: #ffffff;
    padding: 1.5px 0;
  }

  .mk-tier2-th {
    font-size: 8pt;
    font-weight: 700;
    border-bottom: 1px solid $border-navy;
    background-color: #ffffff;
    padding: 1.5px 0;
  }

  .mk-col-cell {
    background-color: $light-blue;
    color: #ffffff;
    text-shadow: 0 0 1px rgba(0, 0, 100, 0.5);
    font-size: 7.5pt;
    font-weight: 800;
    padding: 2.5px 2px;
    line-height: 1.1;
  }
}

/* Shared Column Widths for Alignment */
.col-w-assess { width: 19%; }
.col-w-exam { width: 19%; }
.col-w-con { width: 24%; }
.col-w-high { width: 19%; }
.col-w-avg { width: 19%; }

/* ── Subjects Section (#DBEEF4 Bar with White Marks Cells) ── */
.subjects-container {
  display: flex;
  flex-direction: column;
  gap: 3px;
  margin-bottom: 4px;
}

.subject-item {
  display: flex;
  flex-direction: column;
}

.subject-bar-row {
  display: flex;
  align-items: center;
  background-color: $subject-row-bg; /* #DBEEF4 */
  border-top: 1px solid #758FBE;
  border-bottom: 1px solid #758FBE;
  padding: 1px 0;
}

.subject-name-area {
  width: 25%;
  font-size: 8.5pt;
  font-weight: 800;
  color: #000000;
  padding: 0 6px;
}

.subject-marks-area {
  width: 75%;
  display: flex;
  align-items: center;
}

.mark-box {
  background-color: #ffffff;
  border: 1px solid $border-navy;
  margin-right: -1px; /* seamless connected border */
  text-align: center;
  font-size: 8pt;
  font-weight: 700;
  color: #000000;
  padding: 1.5px 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.subject-remark-line {
  font-size: 7.5pt;
  color: #000000;
  line-height: 1.25;
  padding: 2px 4px 1px 4px;

  &.urdu-remark {
    direction: rtl;
    text-align: right;
    font-family: 'Jameel Noori Nastaleeq', 'Noto Nastaliq Urdu', 'Urdu Typesetting', 'Nafees Web Naskh', Tahoma, sans-serif;
    font-size: 8.5pt;
    line-height: 1.35;
  }
}

/* ── Performance Panels (General & Co-Curricular) ── */
.performance-panel {
  border: 1.5px solid #758FBE;
  padding: 2px 6px;
  margin-bottom: 4px;
}

.panel-heading {
  font-size: 9.5pt;
  font-weight: 800;
  color: $heading-gp-cc; /* #758FBE */
  margin-bottom: 1px;
  line-height: 1.1;
}

.panel-table {
  width: 100%;
  border-collapse: collapse;

  .panel-th {
    font-size: 7.5pt;
    font-weight: 800;
    color: $subheading-gp-cc; /* #000066 */
    text-align: center;
    padding: 1px 2px;
    border-right: 1px dashed #758FBE;

    &:last-child {
      border-right: none;
    }
  }

  .panel-td {
    font-size: 8pt;
    font-weight: 700;
    color: #000000;
    text-align: center;
    padding: 1px 2px;
    border-right: 1px dashed #758FBE;

    &:last-child {
      border-right: none;
    }
  }
}

.cocurriculum-table {
  .cc-th-label,
  .cc-td-label {
    width: 14%;
    text-align: left;
    font-size: 7.5pt;
    font-weight: 800;
    color: #000000;
    padding-left: 2px;
  }
}

/* ── Result Summary Bar ── */
.result-summary-bar {
  width: 100%;
}

.summary-table {
  width: 100%;
  border-collapse: collapse;
  border: 1.5px solid $border-navy;

  .summary-head-row th {
    background-color: $light-blue;
    border: 1px solid $border-navy;
    color: #ffffff;
    text-shadow: 0 0 1px rgba(0, 0, 100, 0.6);
    font-size: 8.5pt;
    font-weight: 800;
    text-align: center;
    padding: 2.5px 2px;
    width: 16.66%;
  }

  .summary-data-row td {
    border: 1px solid $border-navy;
    background-color: #ffffff;
    color: #000000;
    font-size: 8.5pt;
    font-weight: 800;
    text-align: center;
    padding: 2.5px 2px;

    &.status-badge {
      font-weight: 900;
    }
  }
}

/* ═══════════════════════════════════════════════════════════════
   PAGE 2 (BACK SIDE) EXACT STYLES
   ═══════════════════════════════════════════════════════════════ */

.page-back-inner {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

/* ── Self Assessment Box ── */
.self-assessment-wrapper {
  width: 100%;
  border: 1.5px solid #758FBE;
  padding: 3px;
}

.self-assessment-frame {
  border: 1px solid #7FD2F0;
  min-height: 95px;
  padding: 4px 8px;
  display: flex;
  flex-direction: column;
}

.self-assessment-title {
  text-align: center;
  font-size: 11pt;
  font-weight: 800;
  color: #00a2e8;
  letter-spacing: 1px;
}

.self-assessment-body {
  font-size: 8.5pt;
  color: #000000;
  flex: 1;
}

/* ── Grading Key Table ── */
.grading-key-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: 3px 0;
}

.grading-key-title-bar {
  background-color: $light-blue;
  width: 75%;
  text-align: center;
  font-size: 10pt;
  font-weight: 800;
  color: #00a2e8;
  padding: 2.5px 0;
  letter-spacing: 1px;
  border-top: 1.5px solid $border-navy;
  border-left: 1.5px solid $border-navy;
  border-right: 1.5px solid $border-navy;
}

.grading-key-table {
  width: 75%;
  border-collapse: collapse;
  border: 1.5px solid $border-navy;

  th {
    border: 1px solid $border-navy;
    font-size: 8.5pt;
    font-weight: 800;
    text-align: center;
    padding: 2px 4px;
    color: #000000;
  }

  td {
    border: 1px solid $border-navy;
    font-size: 8pt;
    padding: 1.5px 4px;
    color: #000000;
  }

  .gk-th-grade,
  .gk-td-grade {
    width: 35%;
    text-align: center;
    font-weight: 700;
  }

  .gk-th-pct,
  .gk-td-pct {
    width: 65%;
    text-align: center;
    font-weight: 600;
  }
}

/* ── Remarks Sections (Class Teacher & Principal) ── */
.remarks-section {
  border: 1px solid transparent;
  padding: 5px 8px;
  margin: 2px 0;

  &.class-teacher-remarks {
    background-color: #ffffff;
  }

  &.principal-remarks-box {
    background-color: $principal-bg; /* #BACCE4 as specified */
    border: 1px solid #a8bbd8;
  }
}

.remarks-title {
  font-size: 9pt;
  font-weight: 800;
  color: $border-navy;
  margin-bottom: 3px;
  letter-spacing: 0.5px;
}

.remarks-content {
  font-size: 8.5pt;
  line-height: 1.35;
  color: #000000;
  min-height: 26px;
}

.remarks-sig-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  margin-top: 5px;
}

.sig-item {
  display: flex;
  align-items: flex-end;
  width: 45%;

  &.sig-date-item {
    width: 35%;
  }
}

.sig-label {
  font-size: 8.5pt;
  font-weight: 700;
  color: #000000;
  margin-right: 4px;
  white-space: nowrap;
}

.sig-underline {
  border-bottom: 1px solid #000000;
  flex: 1;
  min-height: 14px;
}

/* ── Next Term Date Line ── */
.next-term-section {
  display: flex;
  align-items: flex-end;
  justify-content: center;
  margin: 5px 0;
  padding: 0 16px;
}

.next-term-txt {
  font-size: 9.5pt;
  font-weight: 800;
  color: #000000;
  margin-right: 8px;
  white-space: nowrap;
}

.next-term-underline {
  border-bottom: 1px solid #000000;
  flex: 1;
  max-width: 320px;
  min-height: 16px;
  text-align: center;
  font-size: 9pt;
  font-weight: 700;
}

/* ── Rules Section ── */
.rules-panel {
  border: 1.5px solid #758FBE;
  padding: 5px 10px;
  margin: 2px 0;
}

.rules-title {
  text-align: center;
  font-size: 10pt;
  font-weight: 800;
  color: #00a2e8;
  letter-spacing: 1px;
  margin-bottom: 3px;
}

.rules-ul {
  margin: 0;
  padding-left: 14px;
  list-style-type: disc;

  .rules-li {
    font-size: 7.5pt;
    line-height: 1.3;
    color: #000000;
    margin-bottom: 2px;
  }
}

.sub-rules-ul {
  margin: 1px 0;
  padding-left: 14px;
  list-style-type: square;

  .sub-rules-li {
    font-size: 7.5pt;
    line-height: 1.25;
    color: #000000;
  }
}

/* ── Parent Signature Line ── */
.parent-sig-section {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  margin-top: 5px;
  padding: 0 4px;
}

/* ═══════════════════════════════════════════════════════════════
   PRINT MEDIA QUERY
   ═══════════════════════════════════════════════════════════════ */
@media print {
  @page {
    size: A4 portrait;
    margin: 0;
  }

  html,
  body {
    margin: 0 !important;
    padding: 0 !important;
    background: #ffffff !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }

  .no-print {
    display: none !important;
  }

  .scientist-term-result-wrapper {
    background: #ffffff !important;
    padding: 0 !important;
    min-height: auto !important;
  }

  .print-container {
    padding: 0 !important;
    gap: 0 !important;
  }

  .student-report-set {
    gap: 0 !important;
  }

  .a4-page {
    box-shadow: none !important;
    margin: 0 !important;
    padding: 5mm 6mm !important;
    width: 210mm !important;
    height: 297mm !important;
    min-height: 297mm !important;
    max-height: 297mm !important;
    page-break-after: always !important;
    break-after: page !important;
    box-sizing: border-box !important;
    background: #ffffff !important;

    &:last-child {
      page-break-after: auto !important;
      break-after: auto !important;
    }
  }

  .page-outer-frame {
    border: 4px solid $border-navy !important;
    box-sizing: border-box !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
}
</style>
